<template>
  <div class="layer-filters">
    <FilterRow
      v-for="applied in appliedFilters"
      :key="applied.key"
      :label="applied.filter.label"
      :summary="applied.summary"
      :filter="applied.spec"
      :components="specComponents"
      :active="openKey === applied.key"
      :test-id="`layer-filter-${applied.filter.field}`"
      @edit="openRow(applied.subLayer, applied.filter)"
      @close="closeRow"
      @update="(spec) => applyFilter(applied.subLayer, applied.filter, fromSpecFilter(applied.filter, spec))"
      @clear="applyFilter(applied.subLayer, applied.filter, null)"
    />
    <FilterRow
      v-if="newFilter"
      :label="newFilter.filter.label"
      :filter="newFilter.spec"
      :components="specComponents"
      :active="true"
      :test-id="`layer-filter-${newFilter.filter.field}`"
      @close="cancelNewFilter"
      @update="applyNewFilter"
    />
    <AddFilterButton
      :filter-groups="availableGroups"
      data-testid="add-layer-filter"
      @add-new-filter="addNewFilter"
    />
  </div>
</template>

<script lang="ts">
import {defineComponent} from 'vue';
import type {PropType} from 'vue';
import {mapActions} from 'vuex';

import {
  describeFilterValue,
  fromSpecFilter,
  isFilterActive,
  toSpecFilter,
} from '@/lib/filters/layers/layerSpecFilters';
import type {LayerFilter, LayerFilterValue} from '@/lib/filters/layers/layerFilters';
import type {SpecFilter} from '@/lib/filters/types';
import type {LayerOption, MapLayer} from '@/services/GraphicService/MapLayer';

import AddFilterButton from '@/components/Filters/AddFilterButton.vue';
import FilterRow from '@/components/Filters/FilterRow.vue';
import FilterSpecAutocomplete from '@/components/Filters/FilterSpecAutocomplete.vue';
import FilterSpecCheckboxes from '@/components/Filters/FilterSpecCheckboxes.vue';
import FilterSpecRange from '@/components/Filters/FilterSpecRange.vue';
import FilterSpecText from '@/components/Filters/FilterSpecText.vue';

type SubLayer = LayerOption['subLayers'][number];

/** A filter the user has added, paired with the sub-layer that declared it. */
interface AppliedFilter {
  subLayer: SubLayer;
  filter: LayerFilter;
  value: LayerFilterValue;
  key: string;
  spec: SpecFilter;
  summary: string;
}

const SPEC_COMPONENTS = {
  range: FilterSpecRange,
  text: FilterSpecText,
  options: FilterSpecCheckboxes,
  chips: FilterSpecAutocomplete,
};

export default defineComponent({
  name: 'LayerFilterList',
  components: {
    AddFilterButton,
    FilterRow,
  },
  props: {
    layer: {
      type: Object as PropType<MapLayer>,
      required: true,
    },
  },
  data() {
    return {
      openKey: null as string | null,
      /** The filter being added, held here until it is applied so a cancel stores nothing. */
      newFilter: null as {subLayer: SubLayer; filter: LayerFilter; spec: SpecFilter} | null,
    };
  },
  computed: {
    specComponents: () => SPEC_COMPONENTS,
    filterableSubLayers(): SubLayer[] {
      return this.layer.configuredSubLayers.filter((subLayer) => subLayer.filters?.length);
    },
    /** A row per filter the user has added, in config order. */
    appliedFilters(): AppliedFilter[] {
      return this.filterableSubLayers.flatMap((subLayer) => (subLayer.filters ?? []).flatMap((filter) => {
        const value = subLayer.filterValues?.[filter.field];
        if (!value) return [];
        return [{
          subLayer,
          filter,
          value,
          key: this.keyFor(subLayer, filter),
          spec: toSpecFilter(filter, value),
          summary: describeFilterValue(filter, value),
        }];
      }));
    },
    /** Every filter the layer offers, with the sub-layer that declared it, in config order. */
    offeredFilters(): {subLayer: SubLayer; filter: LayerFilter}[] {
      return this.filterableSubLayers.flatMap((subLayer) =>
        (subLayer.filters ?? []).map((filter) => ({subLayer, filter})));
    },
    /** Those same filters under their headings, each heading first appearing where its first filter does. */
    availableGroups(): {label: string; filters: LayerFilter[]}[] {
      const groups: {label: string; filters: LayerFilter[]}[] = [];
      this.offeredFilters.forEach(({subLayer, filter}) => {
        const label = this.groupLabel(subLayer, filter);
        const group = groups.find((one) => one.label === label);
        if (group) {
          group.filters.push(filter);
        } else {
          groups.push({label, filters: [filter]});
        }
      });
      return groups;
    },
  },
  methods: {
    ...mapActions('layers', ['updateUserLayer']),
    fromSpecFilter,
    keyFor(subLayer: SubLayer, filter: LayerFilter): string {
      return `${subLayer.id}|${filter.field}`;
    },
    subLayerLabel(subLayer: SubLayer): string {
      return subLayer.label ?? subLayer.id;
    },
    /**
     * The heading a filter is listed under: its own group, or the sub-layer that declared it. A
     * single sub-layer with no groups gets no heading — its name would only repeat the layer's own.
     */
    groupLabel(subLayer: SubLayer, filter: LayerFilter): string {
      if (filter.group) return filter.group;
      return this.filterableSubLayers.length > 1 ? this.subLayerLabel(subLayer) : '';
    },
    /**
     * `label` is the chosen filter's label, as the dropdown shows it, and `groupLabel` the heading
     * it sat under. Both are needed because two sub-layers can offer the same filter.
     */
    addNewFilter(label: string, groupLabel: string) {
      const found = this.offeredFilters.find(({subLayer, filter}) =>
        filter.label === label && this.groupLabel(subLayer, filter) === groupLabel);
      if (!found || found.subLayer.filterValues?.[found.filter.field]) return;

      this.newFilter = {...found, spec: toSpecFilter(found.filter)};
      this.openRow(found.subLayer, found.filter);
    },
    applyNewFilter(spec: SpecFilter) {
      if (!this.newFilter) return;
      const {subLayer, filter} = this.newFilter;
      this.applyFilter(subLayer, filter, fromSpecFilter(filter, spec));
    },
    cancelNewFilter() {
      this.newFilter = null;
      this.closeRow();
    },
    openRow(subLayer: SubLayer, filter: LayerFilter) {
      this.openKey = this.keyFor(subLayer, filter);
    },
    closeRow() {
      this.openKey = null;
    },
    /** A null `value` clears the filter. */
    applyFilter(subLayer: SubLayer, filter: LayerFilter, value: LayerFilterValue | null) {
      // A filter the user left empty stores nothing, so no row is left behind that cannot be removed.
      const applied = value != null && isFilterActive(filter, value) ? value : null;
      const values = {...subLayer.filterValues};
      if (applied) {
        values[filter.field] = applied;
      } else {
        delete values[filter.field];
      }

      // Replaces the whole set for the sub-layer, then flushes the layer's state to the user's
      // saved settings. Mutating the live layer first is what makes the flush pick the change up.
      this.layer.setSubLayerFilterValues(subLayer.id, values);
      this.updateUserLayer({id: this.layer.id, options: this.layer.options});
      this.newFilter = null;
      if (!applied) {
        this.closeRow();
      }
    },
  },
});
</script>

<style scoped lang="scss">
.layer-filters {
  text-align: left;
  margin: -0.75rem;

  :deep(.filter-options) {
    left: 4px;
    right: auto;
  }
}
</style>
